import React, { useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FORM_FIELDS } from "@features/vessel-call/create/constants.ts";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer.tsx";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import type { VesselCallFormData } from "@features/vessel-call/create/schema/vesselCallFormSchema.ts";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { buildPortOptions } from "@features/vessel-call/create/utils/portOptions.ts";
import { SasTokenPrefix } from "@services/user/types";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const FalForm1: React.FC = () => {
  const { formData, updateFormData, fetchedPorts } = useOutletContext<VesselCallOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const documentConditions = {
    ...FILE_UPLOAD_PROFILES.certificates,
    hideErrors: false,
  };

  const portOptions = useMemo(() => buildPortOptions(fetchedPorts), [fetchedPorts]);

  const formStore = useVesselCallFormStore;

  const completedAndSignedDocumentUrl = useDocumentUpload(
    FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  return (
    <FormProvider {...formData}>
      <h2 className="text-xl mb-6 mt-6">{t("vessel-call.falform1.title")}</h2>
      <FormSection title={t("vessel-call.falform1.general-declaration-details")}>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NAME_AND_TYPE_OF_SHIP>
          name={FORM_FIELDS.NAME_AND_TYPE_OF_SHIP}
          type="text"
          maxChars={80}
          label={t("vessel-call.falform1.name-and-type-of-ship")}
          testId="falform1-nameAndTypeOfShip"
          onFieldChange={updateFormData}
          disabled
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.IMO_NUMBER>
          name={FORM_FIELDS.IMO_NUMBER}
          type="number"
          label={t("vessel-call.falform1.imo-number")}
          testId="falform1-imoNumber"
          maxDigits={8}
          onFieldChange={updateFormData}
          disabled
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.CALL_SIGN>
          name={FORM_FIELDS.CALL_SIGN}
          type="text"
          maxChars={10}
          label={t("vessel-call.falform1.call-sign")}
          testId="falform1-callSign"
          enforceUppercase
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.VOYAGE_NUMBER>
          name={FORM_FIELDS.VOYAGE_NUMBER}
          type="text"
          maxChars={20}
          label={t("vessel-call.falform1.voyage-number")}
          testId="falform1-voyageNumber"
          enforceUppercase
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.PORT_OF_ARRIVAL>
          name={FORM_FIELDS.PORT_OF_ARRIVAL}
          type="autocomplete"
          maxChars={100}
          options={portOptions}
          label={t("vessel-call.falform1.port-of-arrival")}
          testId="falform1-portOfArrival"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.DATE_TIME_OF_ARRIVAL>
          required
          name={FORM_FIELDS.DATE_TIME_OF_ARRIVAL}
          type="date-picker"
          label={t("vessel-call.falform1.date-time-of-arrival")}
          testId="falform1-dateTimeOfArrival"
          dateConstraint="not-past"
          timePicker
          combinedInput
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.FLAG_STATE_OF_SHIP>
          name={FORM_FIELDS.FLAG_STATE_OF_SHIP}
          type="text"
          maxChars={20}
          label={t("vessel-call.falform1.flag-state-of-ship")}
          testId="falform1-flagStateOfShip"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NAME_OF_MASTER>
          name={FORM_FIELDS.NAME_OF_MASTER}
          type="text"
          maxChars={50}
          label={t("vessel-call.falform1.name-of-master")}
          testId="falform1-nameOfMaster"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.LAST_PORT_OF_CALL>
          name={FORM_FIELDS.LAST_PORT_OF_CALL}
          type="autocomplete"
          options={portOptions}
          label={t("vessel-call.falform1.last-port-of-call")}
          testId="falform1-lastPortOfCall"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.CERTIFICATE_OF_REGISTRY>
          name={FORM_FIELDS.CERTIFICATE_OF_REGISTRY}
          type="text"
          maxChars={100}
          label={t("vessel-call.falform1.certificate-of-registry")}
          testId="falform1-certificateOfRegistry"
          onFieldChange={updateFormData}
        />
        <div className="md:col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.SHIP_AGENT_CONTACT>
            name={FORM_FIELDS.SHIP_AGENT_CONTACT}
            type="text"
            maxChars={200}
            label={t("vessel-call.falform1.ship-agent-contact")}
            testId="falform1-shipAgentContact"
            onFieldChange={updateFormData}
          />
        </div>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.GROSS_TONNAGE>
          name={FORM_FIELDS.GROSS_TONNAGE}
          type="number"
          label={t("vessel-call.falform1.gross-tonnage")}
          testId="falform1-grossTonnage"
          onFieldChange={updateFormData}
          maxDigits={15}
          canBeginWithZero={false}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NET_TONNAGE>
          name={FORM_FIELDS.NET_TONNAGE}
          type="number"
          label={t("vessel-call.falform1.net-tonnage")}
          testId="falform1-netTonnage"
          onFieldChange={updateFormData}
          maxDigits={15}
          canBeginWithZero={false}
        />
        <div className="md:col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.VOYAGE_PARTICULARS>
            name={FORM_FIELDS.VOYAGE_PARTICULARS}
            type="textarea"
            maxChars={1000}
            label={t("vessel-call.falform1.voyage-particulars")}
            validationLabel={t("vessel-call.falform1.voyage-particulars-short")}
            testId="falform1-voyageParticulars"
            onFieldChange={updateFormData}
            showCharCounter
          />
        </div>
        <div className="md:col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.CARGO_DESCRIPTION>
            name={FORM_FIELDS.CARGO_DESCRIPTION}
            type="textarea"
            maxChars={1000}
            label={t("vessel-call.falform1.cargo-description")}
            testId="falform1-cargoDescription"
            onFieldChange={updateFormData}
            showCharCounter
          />
        </div>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NUMBER_OF_CREW>
          name={FORM_FIELDS.NUMBER_OF_CREW}
          type="number"
          label={t("vessel-call.falform1.number-of-crew")}
          testId="falform1-numberOfCrew"
          slotProps={{ htmlInput: { placeholder: "0" } }}
          onFieldChange={updateFormData}
          maxDigits={5}
          canBeginWithZero={false}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NUMBER_OF_PASSENGERS>
          name={FORM_FIELDS.NUMBER_OF_PASSENGERS}
          type="number"
          label={t("vessel-call.falform1.number-of-passengers")}
          testId="falform1-numberOfPassengers"
          slotProps={{ htmlInput: { placeholder: "0" } }}
          onFieldChange={updateFormData}
          maxDigits={5}
          canBeginWithZero={false}
        />
        <div className="md:col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.REMARKS>
            name={FORM_FIELDS.REMARKS}
            type="textarea"
            maxChars={500}
            label={t("vessel-call.falform1.remarks")}
            testId="falform1-remarks"
            onFieldChange={updateFormData}
            minRows={3}
            showCharCounter
          />
        </div>
        <div className="md:col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.WASTERESIDUES_REQUIREMENTS>
            name={FORM_FIELDS.WASTERESIDUES_REQUIREMENTS}
            type="textarea"
            maxChars={500}
            label={t("vessel-call.falform1.ship-requirements")}
            testId="falform1-wasteResidueRequirements"
            onFieldChange={updateFormData}
            showCharCounter
          />
        </div>
      </FormSection>
      <div className="col-span-full mt-8">
        <FormSection title={t(`vessel-call.falform1.completed-signed-form`)}>
          <div className="col-span-full">
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL>
              name={FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL}
              type="file"
              uploadConditions={documentConditions}
              testId="falform1-completed-signed-document"
              files={completedAndSignedDocumentUrl.uploadEntries}
              setFiles={(files) => {
                completedAndSignedDocumentUrl.setUploadEntries(files ?? []);
              }}
              onUpload={(_, files) => completedAndSignedDocumentUrl.handleUpload(files)}
              onFieldChange={updateFormData}
            />
            {completedAndSignedDocumentUrl.uploadEntries.length > 0 && (
              <div className="flex-1">
                <SingleFile
                  isLoading={completedAndSignedDocumentUrl.isPending}
                  testId="falform1-completed-signed-document-preview"
                  file={completedAndSignedDocumentUrl.uploadEntries[0]}
                  fieldName={FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL}
                  setFiles={() => completedAndSignedDocumentUrl.setUploadEntries([])}
                  removeFn={() => {
                    completedAndSignedDocumentUrl.handleRemove();
                  }}
                />
              </div>
            )}
          </div>
        </FormSection>
      </div>
    </FormProvider>
  );
};

export default FalForm1;
